/**
 * EmpresasPage - Página de gestión de empresas
 * Solo accesible para usuarios con rol administrador
 */
import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { PageHeader, DataTable, LoadingSpinner } from '../components/common';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import catalogoService from '../services/catalogoService';

// Constante para roles
const ROLES = {
  ADMINISTRADOR: 'administrador',
  CAJERO: 'cajero',
  CONTADOR: 'contador',
  AUDITOR: 'auditor',
};

const EmpresasPage = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    ruc: '',
    direccion: '',
    telefono: '',
    email: '',
    activo: true,
  });

  // Verificar que el usuario sea administrador
  useEffect(() => {
    if (user && user.rol !== ROLES.ADMINISTRADOR) {
      showError('No tiene permisos para acceder a esta página');
      window.history.back();
    }
  }, [user, showError]);

  useEffect(() => {
    loadEmpresas();
  }, []);

  const loadEmpresas = async () => {
    try {
      setLoading(true);
      const data = await catalogoService.getEmpresas();
      setEmpresas(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error al cargar empresas:', error);
      showError('Error al cargar empresas');
      setEmpresas([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (mode, empresa = null) => {
    setDialogMode(mode);
    setSelectedEmpresa(empresa);

    if (mode === 'edit' && empresa) {
      setFormData({
        nombre: empresa.nombre || '',
        ruc: empresa.ruc || '',
        direccion: empresa.direccion || '',
        telefono: empresa.telefono || '',
        email: empresa.email || '',
        activo: empresa.activo !== undefined ? empresa.activo : true,
      });
    } else {
      setFormData({
        nombre: '',
        ruc: '',
        direccion: '',
        telefono: '',
        email: '',
        activo: true,
      });
    }

    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEmpresa(null);
    setFormData({
      nombre: '',
      ruc: '',
      direccion: '',
      telefono: '',
      email: '',
      activo: true,
    });
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      // Validación básica
      if (!formData.nombre || !formData.ruc) {
        showError('Nombre y RUC son requeridos');
        return;
      }

      setLoading(true);

      if (dialogMode === 'create') {
        await catalogoService.createEmpresa(formData);
        showSuccess('Empresa creada exitosamente');
      } else {
        await catalogoService.updateEmpresa(selectedEmpresa.id, formData);
        showSuccess('Empresa actualizada exitosamente');
      }

      handleCloseDialog();
      loadEmpresas();
    } catch (error) {
      console.error('Error al guardar empresa:', error);
      const errorMessage = error.response?.data?.error || 'Error al guardar empresa';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (empresa) => {
    if (!window.confirm(`¿Está seguro de marcar como inactiva la empresa "${empresa.nombre}"?`)) {
      return;
    }

    try {
      setLoading(true);
      await catalogoService.deleteEmpresa(empresa.id);
      showSuccess('Empresa marcada como inactiva');
      loadEmpresas();
    } catch (error) {
      console.error('Error al eliminar empresa:', error);
      const errorMessage = error.response?.data?.error || 'Error al eliminar empresa';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Columnas de la tabla
  const columns = [
    {
      field: 'nombre',
      headerName: 'Nombre',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'ruc',
      headerName: 'RUC',
      width: 150,
    },
    {
      field: 'direccion',
      headerName: 'Dirección',
      flex: 1,
      minWidth: 200,
      render: (value) => value || '-',
    },
    {
      field: 'telefono',
      headerName: 'Teléfono',
      width: 120,
      render: (value) => value || '-',
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 200,
      render: (value) => value || '-',
    },
    {
      field: 'activo',
      headerName: 'Estado',
      width: 100,
      align: 'center',
      render: (value) => (
        <Chip
          label={value ? 'Activo' : 'Inactivo'}
          color={value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      align: 'center',
      sortable: false,
      render: (value, row) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleOpenDialog('edit', row)}
            color="primary"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(row)}
            color="error"
            disabled={!row.activo}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading && empresas.length === 0) {
    return <LoadingSpinner message="Cargando empresas..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Gestión de Empresas"
        subtitle="Administración de empresas del sistema"
        breadcrumbs={[
          { label: 'Inicio', to: '/dashboard' },
          { label: 'Administración' },
          { label: 'Empresas' },
        ]}
        actions={[
          {
            label: 'Nueva Empresa',
            onClick: () => handleOpenDialog('create'),
            icon: AddIcon,
            variant: 'contained',
            color: 'primary',
          },
        ]}
      />

      <DataTable
        columns={columns}
        data={Array.isArray(empresas) ? empresas : []}
        loading={loading}
        emptyMessage="No hay empresas registradas"
      />

      {/* Dialog para Crear/Editar */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === 'create' ? 'Nueva Empresa' : 'Editar Empresa'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Nombre"
              value={formData.nombre}
              onChange={(e) => handleChange('nombre', e.target.value)}
              fullWidth
              required
            />

            <TextField
              label="RUC"
              value={formData.ruc}
              onChange={(e) => handleChange('ruc', e.target.value)}
              fullWidth
              required
            />

            <TextField
              label="Dirección"
              value={formData.direccion}
              onChange={(e) => handleChange('direccion', e.target.value)}
              fullWidth
              multiline
              rows={2}
            />

            <TextField
              label="Teléfono"
              value={formData.telefono}
              onChange={(e) => handleChange('telefono', e.target.value)}
              fullWidth
            />

            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              fullWidth
            />

            <TextField
              select
              label="Estado"
              value={formData.activo}
              onChange={(e) => handleChange('activo', e.target.value === 'true')}
              fullWidth
            >
              <MenuItem value={true}>Activo</MenuItem>
              <MenuItem value={false}>Inactivo</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {dialogMode === 'create' ? 'Crear' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmpresasPage;
